import { Inject } from '@odinjs/odin';

import { catchRenderException, Endpoint, FormViewService, ProviderAccreditationPendenciesService, ToastService } from '@philips-emr/hpms-provider-commons';
import providerContext from '@philips-emr/hpms-provider-commons/lib/context/provider-context';
import { FetchAPI, FieldAPI, FormAPI, InputAPI, PanelAPI, RadioGroupAPI, RadioGroupItem, ToastNotificationAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnChange, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ChangeEvent, FetchEvent, MountEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { Validator } from '@philips-emr/tws-core/lib/services/model-service';
import { isEmptyArrayOrObject } from '@philips-emr/tws-utils';

import { AccreditationEstablishmentAliases as Aliases, FragmentNames } from '../../codegen';
import BaseController from '../BaseController';

import { AccreditationStatus, AccreditationSteps } from './accreditation-update-controller';

export interface Establishment {
  nrSequence: number;
  ieStatus: AccreditationStatus;
  sgState: string;
  ieTypeSecretary: string;
  ieEstablishment: string;
  message: string;
  ieAction: string;
  ieSus: any;
}

export interface City {
  dsCity: string;
  cdCityIbge: string;
}

enum IeTypeSecretary {
  STATE = 'E',
  MUNICIPAL = 'M',
}

enum IeEstablishment {
  HEAD = 'M',
  BRANCH = 'F',
}

@Controller({ fragment: FragmentNames.ACCREDITATION_ESTABLISHMENT })
class AccreditationEstablishmentController extends BaseController {

  @Inject(Aliases.RADIO_GROUP_IE_TYPE_SECRETARY)
  private radioGroupIeTypeSecretary: RadioGroupAPI;
  @Inject(Aliases.RADIO_GROUP_ESTABLISHMENT_TYPE)
  private radioGroupEstablishmentType: RadioGroupAPI;
  @Inject(Aliases.FETCH_CITY)
  private fetchCity: FetchAPI;
  @Inject(Aliases.FORM_ESTABLISHMENT)
  private formEstablishment: FormAPI;
  @Inject(Aliases.PANEL_PENDENCIES)
  private panelPendencies: PanelAPI;
  @Inject(Aliases.TOAST_NOTIFICATION)
  private toast: ToastNotificationAPI;
  @Inject(Aliases.FIELD_CNPJ_MATRIZ)
  private headquartersIdField: FieldAPI;
  @Inject(Aliases.PANEL_CNES)
  private panelCNES: PanelAPI;
  @Inject(Aliases.INPUT_CPNPJ_MATRIZ)
  private headquartersIdInput: InputAPI;

  private city: City | undefined;

  private readonly ieTypeSecretaryMunicipal: RadioGroupItem = {
    key: IeTypeSecretary.MUNICIPAL,
    label: 'provider.municipal',
    value: IeTypeSecretary.MUNICIPAL,
  };

  private readonly ieTypeSecretaryState: RadioGroupItem = {
    key: IeTypeSecretary.STATE,
    label: 'provider.state-2',
    value: IeTypeSecretary.STATE,
  };

  private readonly ieTypeHead: RadioGroupItem = {
    key: IeEstablishment.HEAD,
    label: 'provider.headquarters',
    value: IeEstablishment.HEAD,
  };

  private readonly ieTypeBranch: RadioGroupItem = {
    key: IeEstablishment.BRANCH,
    label: 'provider.branch',
    value: IeEstablishment.BRANCH,
  };

  @OnMount(Aliases.PANEL_ESTABLISHMENT)
  public onMountPanelProviderAccreditationEstablishment(): void {
    this.city = undefined;
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    this.context.set({ nrSeqProvider, nrSeqAccreditation });

    if (!nrSeqAccreditation) {
      this.setRadioButtonOptions();
      this.setRadioButtonEstablishmentOptions();
      return;
    }
    this.getEstablishmentData(nrSeqProvider, nrSeqAccreditation as number);
  }

  @OnMount(Aliases.FORM_ESTABLISHMENT)
  onMountForm(event: MountEvent): void {
    const api = event.target as unknown as FormAPI;
    FormViewService.shouldEditForm(api, AccreditationSteps.REGISTER_DATA);
  }

  @OnSubmit(Aliases.FORM_ESTABLISHMENT)
  public onSubmitForm(event: FormAPI): void {
    const { record: body } = event;
    const { nrSeqAccreditation, nrSeqTypeAccreditation } = this.historyService.getCurrentRouteParams();
    const ieTypeSecretary = body.ieTypeSecretary as RadioGroupItem;
    const ieEstablishment = body.ieEstablishment as RadioGroupItem;
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;

    this.save(!!nrSeqAccreditation, { nrSeqProvider, nrSeqAccreditation }, { ...body, nrSeqTypeAccreditation, ...this.city, ieEstablishment: ieEstablishment?.key, ieTypeSecretary: ieTypeSecretary?.key });
  }

  @OnMount(Aliases.FETCH_PENDENCIES)
  onMountFetchPendencies(event: MountEvent): void {
    ProviderAccreditationPendenciesService.fetchCardPendencies(event, AccreditationSteps.REGISTER_DATA);
  }

  @OnAfterFetch(Aliases.FETCH_PENDENCIES)
  onAfterFetch(event: FetchEvent): void {
    if (isEmptyArrayOrObject(event.data)) {
      this.panelPendencies.setVisible(false);
    } else {
      ProviderAccreditationPendenciesService.setMessageToastPendency(this.toast, event);
    }
  }

  @OnChange(Aliases.FIELD_ESTABLISHMENT_TYPE)
  onChangeToBranch(event: ChangeEvent<any>): void {
    const data = event.value;
    this.headquartersIdInput.setEnabled(data.value == 'F');

    if (data.value == IeEstablishment.BRANCH) {
      this.headquartersIdField.setValidators([{ name: 'required', options: {} }] as Validator[]);
      this.headquartersIdField.validate();
    } else {
      this.headquartersIdField.setValue('');
      this.headquartersIdField.setValidators([{}] as Validator[]);
      this.headquartersIdField.validate();
      this.headquartersIdField.setState({ showValidations: false });
    }
  }

  private setRadioButtonEstablishmentOptions(): void {
    catchRenderException(() => {
      this.radioGroupEstablishmentType.setData([this.ieTypeHead, this.ieTypeBranch]);
    });
  }

  private setRadioButtonOptions(): void {
    catchRenderException(() => {
      this.radioGroupIeTypeSecretary.setData([this.ieTypeSecretaryMunicipal, this.ieTypeSecretaryState]);
    });
  }

  private setParameterFetchCity(dsFederationUnit: string | undefined): void {
    catchRenderException(() => {
      this.fetchCity.setParameters({ dsFederationUnit });
    });
  }

  private setEstablishmentSelected(ieEstablishment: string, itemSelected: RadioGroupItem): RadioGroupItem {
    if (ieEstablishment === IeEstablishment.HEAD) {
      itemSelected = this.ieTypeHead;
    } else if (ieEstablishment === IeEstablishment.BRANCH) {
      itemSelected = this.ieTypeBranch;
    }

    if (itemSelected) {
      this.radioGroupIeTypeSecretary.setValue(itemSelected);
    }
    return itemSelected;
  }

  private setTypeSecretarySelected(ieTypeSecretary: string, itemSelected: RadioGroupItem): RadioGroupItem {
    if (ieTypeSecretary === IeTypeSecretary.MUNICIPAL) {
      itemSelected = this.ieTypeSecretaryMunicipal;
    } else if (ieTypeSecretary === IeTypeSecretary.STATE) {
      itemSelected = this.ieTypeSecretaryState;
    }

    if (itemSelected) {
      this.radioGroupIeTypeSecretary.setValue(itemSelected);
    }
    return itemSelected;
  }

  private save(isEditing: boolean, params: {}, body: {}): void {
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();

    if (isEditing) {
      HttpService.put(Endpoint.PUT.PROVIDER_ESTABLISHMENT, params, { body }).then(() => {
        this.getEstablishmentData(nrSeqProvider, nrSeqAccreditation as number);
        ToastService.open({ message: MessageService.translate('provider.change-made-successfully'), level: ToastLevel.SUCCESS });
      }).finally(() => this.formEstablishment.setEditing(false));
    }
  }

  private dataLoadedFromCNES(status: AccreditationStatus): void {
    const nrSeqProvider = providerContext.getSelectedProvider().nrSeqProvider;
    const { nrSeqAccreditation } = this.historyService.getCurrentRouteParams();
    HttpService.get<boolean>(Endpoint.GET.DATA_LOADED_FROM_CNES, { nrSeqProvider, nrSeqAccreditation }).then((cnes) => {
      if (cnes.body) {
        this.panelCNES.setVisible(status == AccreditationStatus.AWAITING_REQUESTER);
      }
    }).catch(() => ToastService.open({ message: MessageService.translate('provider.error-process-request'), level: ToastLevel.ERROR }));
  }

  private getEstablishmentData(nrSeqProvider: number, nrSeqAccreditation: number): void {
    HttpService.get<Establishment>(Endpoint.GET.PROVIDER_ESTABLISHMENT, { nrSeqProvider, nrSeqAccreditation })
      .then(resp => {
        const body = resp.body;
        const { sgState, ieTypeSecretary, ieEstablishment, ieStatus, ieSus } = body as unknown as Establishment;
        catchRenderException(() => {
          this.setParameterFetchCity(sgState);
          this.setRadioButtonOptions();
          this.setRadioButtonEstablishmentOptions();
          let itemSelected!: RadioGroupItem;
          this.formEstablishment.setData({ ...body, ieEstablishment: this.setEstablishmentSelected(ieEstablishment, itemSelected), ieTypeSecretary: this.setTypeSecretarySelected(ieTypeSecretary, itemSelected), ieSus: ieSus == 'true' });
          this.formEstablishment.validate();
          this.dataLoadedFromCNES(ieStatus);
          if (ieEstablishment == IeEstablishment.BRANCH) {
            this.headquartersIdField.setValidators([{ name: 'required', options: {} }] as Validator[]);
            this.headquartersIdField.validate();
          } else {
            this.headquartersIdInput.setEnabled(false);
          }

        });
      }).catch(() => ToastService.open({ message: MessageService.translate('provider.error-process-request'), level: ToastLevel.ERROR }));
  }
}

export default AccreditationEstablishmentController;
